/**
 * Heads-up no-limit betting state machine.
 *
 * Pure functional API: `createHand` builds an initial immutable state,
 * `legalActions` enumerates what the player to act may legally do, and
 * `applyAction` returns a brand-new state (the input state is never
 * mutated). This lets callers treat `HandState` as a value they can log,
 * replay, or diff.
 *
 * Chip unit: 0.5bb = 1 chip (see GAME_RULES.md §4), so a 50bb starting
 * stack is 100 chips, SB = 1, BB = 2, and a 1bb-total ante posted by each
 * heads-up player is 1 chip each.
 *
 * Heads-up specifics implemented here:
 *  - Button = small blind. Preflop, the button acts first; postflop, the
 *    non-button (big blind) acts first.
 *  - No side pots are needed (only two players).
 *  - Short-stack posting: if a player's stack can't cover the full
 *    ante/blind, they post whatever they have and are marked all-in.
 *  - Uncalled-bet return: if a bet/raise/blind can't be fully matched by
 *    the opponent because they're all-in for less, the unmatched excess
 *    is returned to the better's stack immediately (never sits in the
 *    pot uncontested).
 *  - Once neither player can act further (one or both are all-in), the
 *    remaining board is dealt out automatically with no further betting
 *    decisions, straight through to showdown.
 *  - Showdown ties (chop): pot is split as evenly as possible; if the
 *    pot doesn't divide evenly, the odd chip goes to the non-button
 *    (out-of-position) player. This is a deliberate house-rule choice
 *    documented in GAME_RULES.md's spirit (not explicitly specified
 *    there) — see README/PLAN notes for rationale if this needs
 *    revisiting.
 */

import { fullDeck, shuffle, type Card, type Rng } from "./cards.js";
import { evaluateJokerDetailed, type BoardReal, type JokerInterpretation } from "./joker.js";

export type PlayerIndex = 0 | 1;

export interface HandConfig {
  /** Starting stack for a fresh match, in chips (0.5bb = 1 chip). */
  startingStackChips: number;
  /** Small blind, in chips. */
  sbChips: number;
  /** Big blind, in chips. */
  bbChips: number;
  /** Ante posted by *each* player, in chips (0 to disable antes). */
  antePerPlayerChips: number;
}

export type Street = "preflop" | "flop" | "turn" | "river";

export interface PlayerHandState {
  readonly hole: readonly [Card, Card];
  readonly stack: number;
  /** Amount committed during the current betting round (street). */
  readonly bet: number;
  /** Total amount committed this hand (antes + all streets). */
  readonly totalCommitted: number;
  readonly folded: boolean;
  readonly isAllIn: boolean;
}

export interface Deal {
  readonly hole: readonly [readonly [Card, Card], readonly [Card, Card]];
  readonly flopReal: readonly [Card, Card];
  readonly turn: Card;
  readonly river: Card;
}

export interface HandResult {
  readonly winner: PlayerIndex | "chop";
  /** Chips each player receives from the pot at resolution. */
  readonly potAwarded: readonly [number, number];
  readonly endedBy: "fold" | "showdown";
  readonly finalStacks: readonly [number, number];
  /** evaluateJoker() comparable values for each player; only present on showdown. */
  readonly showdownValues?: readonly [number, number];
  /**
   * Full "what did the joker turn into" detail per player (category,
   * interpreted card, best 5 cards) -- only present on showdown. Used to
   * render the showdown/hand-history joker-interpretation display.
   */
  readonly showdownDetails?: readonly [JokerInterpretation, JokerInterpretation];
}

export interface ActionLogEntry {
  readonly street: Street;
  readonly player: PlayerIndex;
  readonly type: "fold" | "check" | "call" | "bet" | "raise" | "allIn";
  /** The player's resulting total street-bet after this action (0 for fold/check). */
  readonly amount: number;
}

export interface HandState {
  readonly config: HandConfig;
  /** Player index (0 or 1) holding the button (= small blind). */
  readonly button: PlayerIndex;
  readonly players: readonly [PlayerHandState, PlayerHandState];
  /** Stacks each player started this specific hand with (before antes/blinds). Needed to compute net win/loss for HandRecord. */
  readonly startingStacks: readonly [number, number];
  readonly street: Street;
  readonly deal: Deal;
  readonly minRaiseIncrement: number;
  readonly actedThisStreet: readonly [boolean, boolean];
  readonly toAct: PlayerIndex | null;
  readonly result: HandResult | null;
  /** Ordered log of every voluntary action taken this hand (excludes forced ante/blind posts). */
  readonly actionLog: readonly ActionLogEntry[];
  /** Pot size recorded at the moment each street began (before that street's first action). */
  readonly streetStartPot: { readonly [S in Street]?: number };
}

export type Action =
  | { type: "fold" }
  | { type: "check" }
  | { type: "call" }
  | { type: "bet"; to: number }
  | { type: "raise"; to: number }
  | { type: "allIn" };

export type LegalAction =
  | { type: "fold" }
  | { type: "check" }
  | { type: "call"; amount: number }
  | { type: "bet"; min: number; max: number }
  | { type: "raise"; min: number; max: number }
  | { type: "allIn"; amount: number };

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function other(p: PlayerIndex): PlayerIndex {
  return p === 0 ? 1 : 0;
}

function currentBet(state: HandState): number {
  return Math.max(state.players[0].bet, state.players[1].bet);
}

function isStreetClosed(state: HandState): boolean {
  return (
    state.actedThisStreet[0] &&
    state.actedThisStreet[1] &&
    state.players[0].bet === state.players[1].bet
  );
}

function updatePlayer(
  state: HandState,
  index: PlayerIndex,
  patch: Partial<PlayerHandState>
): HandState {
  const players: [PlayerHandState, PlayerHandState] = [
    state.players[0],
    state.players[1],
  ];
  players[index] = { ...players[index], ...patch };
  return { ...state, players };
}

/** Moves chips from `index`'s stack into their street bet, capped at their stack. */
function commitTo(state: HandState, index: PlayerIndex, to: number): HandState {
  const player = state.players[index];
  const target = Math.min(to, player.bet + player.stack);
  const delta = target - player.bet;
  return updatePlayer(state, index, {
    stack: player.stack - delta,
    bet: target,
    totalCommitted: player.totalCommitted + delta,
    isAllIn: player.stack - delta === 0,
  });
}

/**
 * If a player is all-in for less than the opponent's street bet (and thus
 * can never match it), the opponent's excess is returned to their stack —
 * it was never actually contested. Symmetric: checks both directions.
 */
function settleShortAllIn(state: HandState): HandState {
  let s = state;
  for (const idx of [0, 1] as const) {
    const short = s.players[idx];
    const opp = s.players[other(idx)];
    if (short.stack === 0 && opp.bet > short.bet) {
      const excess = opp.bet - short.bet;
      s = updatePlayer(s, other(idx), {
        stack: opp.stack + excess,
        bet: opp.bet - excess,
        totalCommitted: opp.totalCommitted - excess,
        isAllIn: opp.stack + excess === 0,
      });
    }
  }
  return s;
}

function markActed(state: HandState, index: PlayerIndex, value = true): HandState {
  const actedThisStreet: [boolean, boolean] = [
    state.actedThisStreet[0],
    state.actedThisStreet[1],
  ];
  actedThisStreet[index] = value;
  return { ...state, actedThisStreet };
}

function appendLog(
  state: HandState,
  street: Street,
  player: PlayerIndex,
  type: ActionLogEntry["type"],
  amount: number
): HandState {
  return { ...state, actionLog: [...state.actionLog, { street, player, type, amount }] };
}

// ---------------------------------------------------------------------------
// Hand creation
// ---------------------------------------------------------------------------

/**
 * Deals a fresh hand. `button` selects which player index (0 or 1) is on
 * the button/small blind this hand. `stacks` defaults to
 * `[config.startingStackChips, config.startingStackChips]` but should be
 * passed explicitly by match-management code to carry stacks across
 * hands.
 *
 * `forcedDeal` is an optional escape hatch (used by tests and
 * tooling/debug UIs) to pin the exact hole/board deal instead of drawing
 * it from `rng`. When provided, `rng` is not consulted for dealing.
 */
export function createHand(
  config: HandConfig,
  rng: Rng,
  button: PlayerIndex = 0,
  stacks: readonly [number, number] = [
    config.startingStackChips,
    config.startingStackChips,
  ],
  forcedDeal?: Deal
): HandState {
  let deal: Deal;
  if (forcedDeal) {
    deal = forcedDeal;
  } else {
    const deck = shuffle(fullDeck(), rng);
    deal = {
      hole: [
        [deck[0]!, deck[1]!],
        [deck[2]!, deck[3]!],
      ],
      flopReal: [deck[4]!, deck[5]!],
      turn: deck[6]!,
      river: deck[7]!,
    };
  }

  const bb = other(button);

  let players: [PlayerHandState, PlayerHandState] = [
    {
      hole: deal.hole[0],
      stack: stacks[0],
      bet: 0,
      totalCommitted: 0,
      folded: false,
      isAllIn: false,
    },
    {
      hole: deal.hole[1],
      stack: stacks[1],
      bet: 0,
      totalCommitted: 0,
      folded: false,
      isAllIn: false,
    },
  ];

  let state: HandState = {
    config,
    button,
    players,
    startingStacks: [stacks[0], stacks[1]],
    street: "preflop",
    deal,
    minRaiseIncrement: config.bbChips,
    actedThisStreet: [false, false],
    toAct: null,
    result: null,
    actionLog: [],
    streetStartPot: {},
  };

  // Post antes (dead money, doesn't affect street `bet`/currentBet logic).
  if (config.antePerPlayerChips > 0) {
    for (const idx of [0, 1] as const) {
      const p = state.players[idx];
      const anteAmount = Math.min(config.antePerPlayerChips, p.stack);
      state = updatePlayer(state, idx, {
        stack: p.stack - anteAmount,
        totalCommitted: p.totalCommitted + anteAmount,
        isAllIn: p.stack - anteAmount === 0,
      });
    }
  }

  // Post blinds (button = SB posts first, then BB).
  state = commitTo(state, button, state.players[button].bet + Math.min(config.sbChips, state.players[button].stack));
  state = commitTo(state, bb, state.players[bb].bet + Math.min(config.bbChips, state.players[bb].stack));
  state = settleShortAllIn(state);
  state = { ...state, streetStartPot: { ...state.streetStartPot, preflop: pot(state) } };

  // Preflop: button (SB) acts first.
  return resolveToAct(state, button);
}

// ---------------------------------------------------------------------------
// Turn resolution / street progression
// ---------------------------------------------------------------------------

/**
 * Determines who actually gets to act next, automatically skipping (and
 * marking as "acted") any player who is already all-in and thus
 * physically cannot take an action. Cascades through street advancement
 * (and, if necessary, straight to showdown) when nobody can act further.
 */
function resolveToAct(state: HandState, firstCandidate: PlayerIndex): HandState {
  let s = state;
  let candidate = firstCandidate;
  for (let i = 0; i < 2; i++) {
    if (s.players[candidate].stack > 0) {
      return { ...s, toAct: candidate };
    }
    s = markActed(s, candidate);
    if (isStreetClosed(s)) {
      return advanceStreetOrShowdown(s);
    }
    candidate = other(candidate);
  }
  // Both players are all-in; force-close regardless (settleShortAllIn
  // should already have equalized bets by this point).
  return advanceStreetOrShowdown(markActed(markActed(s, 0), 1));
}

function dealtBoardReal(state: HandState, street: Street): Card[] {
  const { flopReal, turn, river } = state.deal;
  switch (street) {
    case "preflop":
      return [];
    case "flop":
      return [flopReal[0], flopReal[1]];
    case "turn":
      return [flopReal[0], flopReal[1], turn];
    case "river":
      return [flopReal[0], flopReal[1], turn, river];
  }
}

function nextStreet(street: Street): Street | null {
  if (street === "preflop") return "flop";
  if (street === "flop") return "turn";
  if (street === "turn") return "river";
  return null;
}

function advanceStreetOrShowdown(state: HandState): HandState {
  // If either player is already all-in, nobody can be bet against any
  // further this hand (there's no one left who could respond) -- deal
  // straight through every remaining street to showdown without ever
  // assigning toAct, regardless of how much the *other* player still
  // has behind (e.g. after an uncalled-bet refund).
  const noMoreBetting = state.players[0].stack === 0 || state.players[1].stack === 0;

  let s = state;
  for (;;) {
    const next = nextStreet(s.street);
    if (next === null) {
      return resolveShowdown(s);
    }
    s = {
      ...s,
      street: next,
      players: [
        { ...s.players[0], bet: 0 },
        { ...s.players[1], bet: 0 },
      ],
      minRaiseIncrement: s.config.bbChips,
      actedThisStreet: [false, false],
      streetStartPot: { ...s.streetStartPot, [next]: pot(s) },
    };
    if (noMoreBetting) continue;
    // Postflop: non-button (BB) acts first.
    return resolveToAct(s, other(state.button));
  }
}

function pot(state: HandState): number {
  return state.players[0].totalCommitted + state.players[1].totalCommitted;
}

function resolveFoldWin(state: HandState, winner: PlayerIndex): HandState {
  const p = pot(state);
  const folder = other(winner);
  const potAwarded: [number, number] = [0, 0];
  potAwarded[winner] = p;
  const players: [PlayerHandState, PlayerHandState] = [state.players[0], state.players[1]];
  players[folder] = { ...players[folder], folded: true };
  players[winner] = { ...players[winner], stack: players[winner].stack + p };

  const result: HandResult = {
    winner,
    potAwarded,
    endedBy: "fold",
    finalStacks: [players[0].stack, players[1].stack],
  };

  return { ...state, players, toAct: null, result };
}

function resolveShowdown(state: HandState): HandState {
  const board = dealtBoardReal(state, "river") as unknown as BoardReal;
  const d0 = evaluateJokerDetailed(state.players[0].hole as [Card, Card], board);
  const d1 = evaluateJokerDetailed(state.players[1].hole as [Card, Card], board);
  const v0 = d0.value;
  const v1 = d1.value;

  const p = pot(state);
  const potAwarded: [number, number] = [0, 0];
  let winner: PlayerIndex | "chop";

  if (v0 > v1) {
    winner = 0;
    potAwarded[0] = p;
  } else if (v1 > v0) {
    winner = 1;
    potAwarded[1] = p;
  } else {
    winner = "chop";
    const half = Math.floor(p / 2);
    const remainder = p - half * 2;
    const nonButton = other(state.button);
    potAwarded[state.button] = half;
    potAwarded[nonButton] = half + remainder;
  }

  const players: [PlayerHandState, PlayerHandState] = [
    { ...state.players[0], stack: state.players[0].stack + potAwarded[0] },
    { ...state.players[1], stack: state.players[1].stack + potAwarded[1] },
  ];

  const result: HandResult = {
    winner,
    potAwarded,
    endedBy: "showdown",
    finalStacks: [players[0].stack, players[1].stack],
    showdownValues: [v0, v1],
    showdownDetails: [d0, d1],
  };

  return { ...state, street: "river", players, toAct: null, result };
}

// ---------------------------------------------------------------------------
// Legal actions
// ---------------------------------------------------------------------------

interface ActionWindow {
  player: PlayerIndex;
  canFold: boolean;
  canCheck: boolean;
  call: { amount: number } | null;
  openKind: "bet" | "raise" | null;
  min: number;
  max: number;
  allInTo: number;
}

function computeActionWindow(state: HandState): ActionWindow | null {
  if (state.toAct === null || state.result !== null) return null;
  const player = state.toAct;
  const p = state.players[player];
  if (p.stack === 0) return null;

  const cb = currentBet(state);
  const allInTo = p.bet + p.stack;

  if (cb > p.bet) {
    // Facing a bet: fold / call / (raise if reopened and possible).
    const callAmount = Math.min(cb, allInTo) - p.bet;
    const minRaiseTo = Math.min(cb + state.minRaiseIncrement, allInTo);
    const canRaise = allInTo > cb; // has more chips than a plain call requires
    return {
      player,
      canFold: true,
      canCheck: false,
      call: { amount: callAmount },
      openKind: canRaise ? "raise" : null,
      min: minRaiseTo,
      max: allInTo,
      allInTo,
    };
  }

  // Nothing to call (cb === p.bet): check is free, opening bet/raise optional.
  const desiredMin = cb === 0 ? state.config.bbChips : cb + state.minRaiseIncrement;
  const minTo = Math.min(desiredMin, allInTo);
  return {
    player,
    canFold: false,
    canCheck: true,
    call: null,
    openKind: allInTo > cb ? (cb === 0 ? "bet" : "raise") : null,
    min: minTo,
    max: allInTo,
    allInTo,
  };
}

export function legalActions(state: HandState): LegalAction[] {
  const w = computeActionWindow(state);
  if (w === null) return [];

  const actions: LegalAction[] = [];
  if (w.canFold) actions.push({ type: "fold" });
  if (w.canCheck) actions.push({ type: "check" });
  if (w.call) actions.push({ type: "call", amount: w.call.amount });
  if (w.openKind) {
    actions.push({ type: w.openKind, min: w.min, max: w.max });
  }
  // "allIn" is always offered as an explicit convenience action whenever
  // the player has chips; it's equivalent to bet/raise/call to `allInTo`.
  actions.push({ type: "allIn", amount: w.allInTo });
  return actions;
}

// ---------------------------------------------------------------------------
// Applying actions
// ---------------------------------------------------------------------------

export function applyAction(state: HandState, action: Action): HandState {
  const w = computeActionWindow(state);
  if (w === null) {
    throw new Error("No action to apply: hand is over or no player to act.");
  }
  const player = w.player;
  const opp = other(player);

  const street = state.street;

  switch (action.type) {
    case "fold": {
      if (!w.canFold) {
        throw new Error("Fold is not legal (checking is free).");
      }
      const logged = appendLog(state, street, player, "fold", 0);
      return resolveFoldWin(logged, opp);
    }

    case "check": {
      if (!w.canCheck) {
        throw new Error("Check is not legal (facing a bet).");
      }
      let s = appendLog(state, street, player, "check", 0);
      s = markActed(s, player);
      if (isStreetClosed(s)) return advanceStreetOrShowdown(s);
      return resolveToAct(s, opp);
    }

    case "call": {
      if (!w.call) {
        throw new Error("Call is not legal (nothing to call).");
      }
      let s = commitTo(state, player, currentBet(state));
      s = settleShortAllIn(s);
      s = appendLog(s, street, player, "call", s.players[player].bet);
      s = markActed(s, player);
      if (isStreetClosed(s)) return advanceStreetOrShowdown(s);
      return resolveToAct(s, opp);
    }

    case "bet":
    case "raise": {
      if (!w.openKind) {
        throw new Error(`${action.type} is not legal in this state.`);
      }
      if (action.to < w.min || action.to > w.max) {
        throw new Error(
          `${action.type} to ${action.to} is out of legal range [${w.min}, ${w.max}].`
        );
      }
      return applyOpen(state, player, opp, action.to, action.type);
    }

    case "allIn": {
      if (w.allInTo <= currentBet(state)) {
        // Not enough to exceed the current bet: this is really an
        // all-in call for less.
        let s = commitTo(state, player, w.allInTo);
        s = settleShortAllIn(s);
        s = appendLog(s, street, player, "allIn", s.players[player].bet);
        s = markActed(s, player);
        if (isStreetClosed(s)) return advanceStreetOrShowdown(s);
        return resolveToAct(s, opp);
      }
      return applyOpen(state, player, opp, w.allInTo, "allIn");
    }

    default: {
      const _exhaustive: never = action;
      throw new Error(`Unknown action: ${JSON.stringify(_exhaustive)}`);
    }
  }
}

function applyOpen(
  state: HandState,
  player: PlayerIndex,
  opp: PlayerIndex,
  to: number,
  logType: ActionLogEntry["type"]
): HandState {
  const before = currentBet(state);
  const increment = to - before;
  const fullRaise = increment >= state.minRaiseIncrement;
  const street = state.street;

  let s = commitTo(state, player, to);
  s = settleShortAllIn(s);
  s = appendLog(s, street, player, logType, s.players[player].bet);
  s = markActed(s, player);
  // The opponent must respond to a new bet/raise.
  s = markActed(s, opp, false);
  if (fullRaise) {
    s = { ...s, minRaiseIncrement: increment };
  }
  if (isStreetClosed(s)) return advanceStreetOrShowdown(s);
  return resolveToAct(s, opp);
}

// ---------------------------------------------------------------------------
// HandRecord export (full hand history, for UI/hand-review/stats)
// ---------------------------------------------------------------------------

export type Position = "BTN/SB" | "BB";

export interface HandRecordBoard {
  /** Whether the joker (always the first flop card) was revealed, i.e. flop was reached. */
  readonly jokerRevealed: boolean;
  readonly flopReal?: readonly [Card, Card];
  readonly turn?: Card;
  readonly river?: Card;
}

export interface HandRecordStreet {
  readonly potAtStart: number;
  readonly actions: readonly ActionLogEntry[];
}

export interface HandRecord {
  readonly config: HandConfig;
  readonly button: PlayerIndex;
  readonly positions: { readonly [P in PlayerIndex]: Position };
  readonly holeCards: readonly [readonly [Card, Card], readonly [Card, Card]];
  readonly board: HandRecordBoard;
  readonly streets: { readonly [S in Street]?: HandRecordStreet };
  readonly result: HandResult;
  /** finalStacks - startingStacks per player, i.e. this hand's chip win/loss. */
  readonly netResult: readonly [number, number];
}

const ALL_STREETS: readonly Street[] = ["preflop", "flop", "turn", "river"];

/**
 * Builds a complete, JSON-serializable record of a finished hand: config,
 * positions, hole cards, however much of the board was reached, the
 * per-street action log with pot-at-street-start, the showdown/fold
 * result (including each player's joker interpretation on showdown),
 * and each player's net chip result. Throws if the hand isn't over yet.
 */
export function exportHandRecord(state: HandState): HandRecord {
  if (!state.result) {
    throw new Error("Cannot export a HandRecord for a hand that isn't complete yet.");
  }

  const nonButton = other(state.button);
  const positions: { [P in PlayerIndex]: Position } = { 0: "BB", 1: "BB" };
  positions[state.button] = "BTN/SB";
  positions[nonButton] = "BB";

  const jokerRevealed = state.streetStartPot.flop !== undefined;
  const board: HandRecordBoard = {
    jokerRevealed,
    ...(jokerRevealed ? { flopReal: state.deal.flopReal } : {}),
    ...(state.streetStartPot.turn !== undefined ? { turn: state.deal.turn } : {}),
    ...(state.streetStartPot.river !== undefined ? { river: state.deal.river } : {}),
  };

  const streets: { [S in Street]?: HandRecordStreet } = {};
  for (const street of ALL_STREETS) {
    const potAtStart = state.streetStartPot[street];
    if (potAtStart === undefined) continue;
    streets[street] = {
      potAtStart,
      actions: state.actionLog.filter((a) => a.street === street),
    };
  }

  const netResult: [number, number] = [
    state.result.finalStacks[0] - state.startingStacks[0],
    state.result.finalStacks[1] - state.startingStacks[1],
  ];

  return {
    config: state.config,
    button: state.button,
    positions,
    holeCards: [state.players[0].hole, state.players[1].hole],
    board,
    streets,
    result: state.result,
    netResult,
  };
}

// ---------------------------------------------------------------------------
// Match management helpers
// ---------------------------------------------------------------------------

export interface MatchState {
  readonly config: HandConfig;
  readonly button: PlayerIndex;
  readonly stacks: readonly [number, number];
  readonly handNumber: number;
  readonly isOver: boolean;
}

export function createMatch(config: HandConfig, startingButton: PlayerIndex = 0): MatchState {
  return {
    config,
    button: startingButton,
    stacks: [config.startingStackChips, config.startingStackChips],
    handNumber: 0,
    isOver: false,
  };
}

/** Deals the next hand of the match using the match's current stacks/button. */
export function startNextHand(match: MatchState, rng: Rng): HandState {
  if (match.isOver) {
    throw new Error("Match is already over.");
  }
  return createHand(match.config, rng, match.button, match.stacks);
}

/**
 * Folds a completed hand's result back into the match: updates stacks,
 * rotates the button, and detects match-over (a player's stack hits 0).
 */
export function applyHandResultToMatch(match: MatchState, handState: HandState): MatchState {
  if (!handState.result) {
    throw new Error("Hand is not complete yet.");
  }
  const stacks = handState.result.finalStacks;
  const isOver = stacks[0] === 0 || stacks[1] === 0;
  return {
    ...match,
    stacks: [stacks[0], stacks[1]],
    button: other(match.button),
    handNumber: match.handNumber + 1,
    isOver,
  };
}
